import {
    Box,
    Input,
    InputGroup,
    InputRightAddon,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Text,
    VStack,
    useDisclosure,
} from '@chakra-ui/react';
import { useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import {
    EDITABLE_CELL_ACTIVE,
    EDITABLE_CELL_GEOMETRY,
    EDITABLE_CELL_HOVER,
    FIELD_RADII,
    UNIT_FIELD_ADDON_RADII,
    UNIT_FIELD_INPUT_RADII,
} from './constants';
import { numberEntryError } from './SettingsNumberInput';

export interface SettingsInlineEditProps {
    /** Cell content shown as the click target. */
    display: ReactNode;
    ariaLabel?: string;
    /** When disabled, renders the display with no edit affordance. */
    isDisabled?: boolean;
    /** Fires right before the popover opens; seed draft state here. */
    onOpen?: () => void;
    /** Fires on any close (blur, outside click, Enter). Commit drafts here. */
    onClose?: () => void;
    minW?: string;
    /**
     * Which edge of the cell the panel hangs from. Cells near the right edge of
     * a table pass 'bottom-end' so a panel wider than the cell grows left,
     * back over the table, instead of spilling past it.
     */
    placement?: 'bottom-start' | 'bottom-end';
    /**
     * By default the panel is sized to the cell, which popper applies after it
     * has already positioned the panel at its natural width — fine for
     * 'bottom-start', where the anchor is the cell's left edge and the width
     * does not enter the calculation. A 'bottom-end' panel is placed at
     * `cell right - panel width`, so it needs a width that is settled before
     * positioning: pass an explicit `width` and turn this off.
     */
    matchTriggerWidth?: boolean;
    /** Fixed panel width, for the matchTriggerWidth={false} case. */
    width?: string;
    /** Popover body; the function form receives close for save-on-select flows. */
    children: ReactNode | ((close: () => void) => ReactNode);
}

/**
 * Click-to-edit wrapper for a table cell that maps to one simple field. At
 * rest the cell reads as plain text; hovering it draws an input's outline so
 * the field reads as editable before you click. While editing, that same
 * outline turns brand blue and the editor opens in a popover anchored flush
 * below it, which floats and may carry a shadow per the elevation policy.
 * Callers own the draft state: seed it in onOpen and save in onClose (or save
 * explicitly and call close for save-on-select / Apply-button editors).
 */
export const SettingsInlineEdit = ({
    display,
    ariaLabel,
    isDisabled = false,
    onOpen,
    onClose,
    minW = '240px',
    placement = 'bottom-start',
    matchTriggerWidth = true,
    width,
    children,
}: SettingsInlineEditProps) => {
    const disclosure = useDisclosure();

    const handleClose = () => {
        disclosure.onClose();
        onClose?.();
    };

    if (isDisabled) return <>{display}</>;

    return (
        <Popover
            isOpen={disclosure.isOpen}
            onOpen={() => {
                onOpen?.();
                disclosure.onOpen();
            }}
            onClose={handleClose}
            placement={placement}
            // Keep the panel flush with the cell's bottom edge and at least as
            // wide as it, like an expanding cell rather than a floating menu.
            gutter={4}
            matchWidth={matchTriggerWidth}
            isLazy
        >
            <PopoverTrigger>
                <Box
                    as="button"
                    type="button"
                    aria-label={ariaLabel}
                    // Rows in some tables open an edit modal on click; editing
                    // in place should not also trigger that.
                    onClick={(e: MouseEvent) => e.stopPropagation()}
                    textAlign="inherit"
                    display="block"
                    cursor="pointer"
                    {...EDITABLE_CELL_GEOMETRY}
                    {...EDITABLE_CELL_HOVER}
                    // While editing, the cell keeps the input outline in brand blue.
                    {...(disclosure.isOpen ? EDITABLE_CELL_ACTIVE : {})}
                    _hover={disclosure.isOpen ? undefined : EDITABLE_CELL_HOVER._hover}
                >
                    {display}
                </Box>
            </PopoverTrigger>
            <Portal>
                <PopoverContent
                    w={width}
                    minW={minW}
                    maxW="320px"
                    borderColor="gray.200"
                    _focus={{ outline: 'none' }}
                >
                    <PopoverBody p={3}>
                        {typeof children === 'function' ? children(handleClose) : children}
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
};

export interface SettingsInlineNumberEditProps {
    display: ReactNode;
    /** Current value in display units (e.g. whole percent, minutes). */
    value?: number | null;
    /** Called with the new value, or null when the field was cleared. */
    onSave: (next: number | null) => void;
    /** Suffix shown after the input, e.g. "%" or "min". */
    unit?: string;
    min?: number;
    max?: number;
    isDisabled?: boolean;
    ariaLabel?: string;
    placeholder?: string;
    /**
     * Panel width. A number field needs only a few characters, so a cell in a
     * wide column can pass a narrow fixed width instead of letting the panel
     * match the cell.
     */
    width?: string;
    minW?: string;
}

/**
 * Inline editor for a single numeric cell (markup percent, threshold,
 * minutes). Saves on Enter or when the popover closes; an empty input clears
 * the value. Entry is plain text — no stepper arrows — and the field turns red
 * while the typed value sits outside the allowable range, in which case
 * closing the editor discards it rather than clamping.
 */
export const SettingsInlineNumberEdit = ({
    display,
    value,
    onSave,
    unit,
    min,
    max,
    isDisabled = false,
    ariaLabel,
    placeholder,
    width,
    minW = '180px',
}: SettingsInlineNumberEditProps) => {
    const [draft, setDraft] = useState('');
    const current = value ?? null;

    const error = numberEntryError(draft, { min, max, allowDecimals: true });

    const commit = () => {
        const trimmed = draft.trim();
        if (trimmed === '') {
            if (current !== null) onSave(null);
            return;
        }
        // Out-of-range entries are shown in red while editing and dropped on
        // close, so a bad value never lands on the record.
        if (error) return;
        const parsed = Number(trimmed);
        if (parsed !== current) onSave(parsed);
    };

    return (
        <SettingsInlineEdit
            display={display}
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            minW={minW}
            width={width}
            // A fixed width has to be settled before popper positions the
            // panel; matching the cell would override it.
            matchTriggerWidth={width === undefined}
            onOpen={() => setDraft(current === null ? '' : String(current))}
            onClose={commit}
        >
            {(close) => (
                <InputGroup size="sm">
                    <Input
                        // Text, not number: no stepper arrows, no wheel-to-change.
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        autoFocus
                        // The current value comes up selected, so typing replaces
                        // it instead of appending to it.
                        onFocus={(e) => e.target.select()}
                        value={draft}
                        isInvalid={error !== null}
                        aria-invalid={error !== null}
                        placeholder={placeholder}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            // Closing runs commit via onClose.
                            if (e.key === 'Enter') close();
                        }}
                        {...(unit ? UNIT_FIELD_INPUT_RADII : FIELD_RADII)}
                    />
                    {unit && (
                        <InputRightAddon {...UNIT_FIELD_ADDON_RADII}>{unit}</InputRightAddon>
                    )}
                </InputGroup>
            )}
        </SettingsInlineEdit>
    );
};

export interface SettingsInlineTextEditProps {
    display: ReactNode;
    value: string;
    /** Called with the trimmed value when it changed and passed validation. */
    onSave: (next: string) => void;
    placeholder?: string;
    ariaLabel?: string;
    isDisabled?: boolean;
    maxLength?: number;
    /**
     * Returns a message while the typed value is not acceptable — a duplicate
     * name, a malformed email. The field turns red and closing the editor
     * discards the entry rather than saving it.
     */
    validate?: (value: string) => string | null;
    /** Panel width; omit to size the panel to the cell. */
    width?: string;
    minW?: string;
}

/**
 * Inline editor for a single free-text cell (a name, an email). Saves on Enter
 * or when the popover closes; an invalid entry is shown in red and dropped on
 * close, so a bad value never lands on the record.
 */
export const SettingsInlineTextEdit = ({
    display,
    value,
    onSave,
    placeholder,
    ariaLabel,
    isDisabled = false,
    maxLength,
    validate,
    width,
    minW = '240px',
}: SettingsInlineTextEditProps) => {
    const [draft, setDraft] = useState('');

    const error = validate ? validate(draft.trim()) : null;

    const commit = () => {
        const trimmed = draft.trim();
        if (error) return;
        if (trimmed !== value.trim()) onSave(trimmed);
    };

    return (
        <SettingsInlineEdit
            display={display}
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            minW={minW}
            width={width}
            matchTriggerWidth={width === undefined}
            onOpen={() => setDraft(value)}
            onClose={commit}
        >
            {(close) => (
                <VStack align="stretch" spacing={1.5}>
                    <Input
                        size="sm"
                        autoFocus
                        autoComplete="off"
                        // The current value comes up selected, so typing
                        // replaces it instead of appending to it.
                        onFocus={(e) => e.target.select()}
                        value={draft}
                        placeholder={placeholder}
                        maxLength={maxLength}
                        isInvalid={error !== null}
                        aria-invalid={error !== null}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            // Closing runs commit via onClose.
                            if (e.key === 'Enter' && !error) close();
                        }}
                        {...FIELD_RADII}
                    />
                    {error && (
                        <Text fontSize="xs" color="red.500">
                            {error}
                        </Text>
                    )}
                </VStack>
            )}
        </SettingsInlineEdit>
    );
};

export default SettingsInlineEdit;
